import { resetUserLogin } from './query-status';
import { clearAuthToken, getAuthToken } from '@/client/store/auth-token-store';
import { ApiErrorCode, ApiResponse, createApi } from '@/libs/sdk/api';
import { t } from 'i18next';

// Default to same-origin /api (worker mock/proxy) so module evaluation
// does not throw when env is absent (ky requires prefixUrl)
export const apiUrl = import.meta.env.VITE_API_URL || '/api';
// {code: int, reason: string, msg: string, data: {}}

// SIWE Bearer token attach is skipped in mock mode (no real backend auth).
const isMockMode = import.meta.env.VITE_ENABLE_MOCK === 'true';

// Only 401 means the token itself is invalid. 403 can be origin/permission/
// rate-limit rejections (e.g. SSE origin whitelist) — surface the error but
// keep the session, otherwise an unrelated 403 logs the user out.
export const handleUnauthorizedResponse = async (response: Response): Promise<string | null> => {
  if (response.status === 401) {
    clearAuthToken();
    await resetUserLogin();
    return t('error.unauthorized');
  }
  if (response.status === 403) {
    return t('error.unauthorized');
  }
  return null;
};

export const api = createApi({
  prefixUrl: apiUrl,
  timeout: 10e3,
  retry: 0,
  mode: 'cors',
  credentials: 'same-origin',
  hooks: {
    beforeRequest: [
      async request => {
        if (!isMockMode) {
          const token = getAuthToken();
          if (token) request.headers.set('Authorization', `Bearer ${token}`);
        }
        // NOTE: intentionally do NOT stamp a global `BaseTokenAddress` header.
        // AIP + USDT markets coexist in one feed; per-request collateral is
        // scoped via the query's `baseTokenAddress` param (undefined = all).
        // Stamping a global header here narrowed every list/positions response
        // to a single collateral, which breaks the mixed feed.
      },
    ],
    afterResponse: [
      async (_request, _options, response) => {
        let msg = await handleUnauthorizedResponse(response);
        if (!msg && !response.ok) {
          try {
            const body = (await response.json()) as ApiResponse;
            if (body.code !== 0) msg = body.msg;
            switch (body.code) {
              case ApiErrorCode.ErrBindSelfInviteCode:
                msg = t('you-cannot-use-your-own-invite-code');
                break;
            }
          } catch {
            if (response.status >= 500) {
              msg = t('error.server-error');
            } else if (response.status >= 400) {
              msg = response.statusText;
            }
          }
        }
        if (msg) return Promise.reject(new Error(msg, { cause: response.status }));
      },
    ],
    beforeError: [
      error => {
        if (
          error.name === 'TypeError' &&
          (error.message.includes('Failed to fetch') || error.message.includes('Network request failed'))
        ) {
          error.message = t('error.network-disconnected');
        } else if (error.name === 'AbortError') {
          error.message = t('error.request-timeout');
        }
        return error;
      },
    ],
  },
});
